import {Component, EventEmitter, inject, Input, OnDestroy, Output} from '@angular/core';
import {ThaidAuthData} from '../../../pages/user-certificate/user-certificate-modal';
import {CommonModule} from '@angular/common';
import {ConfirmDialogService} from '../dialog/confirm-dialog/confirm-dialog.service';
import {ToastService} from '../toast/toast-service';
import {UserCertificateApiService} from '../../../pages/user-certificate/user-certificate-api.service';
import {CertificateItemDto} from '../../../../models/generated-models';

@Component({
  selector: 'app-certificate-sync-button',
  imports: [CommonModule],
  templateUrl: './certificate-sync-button.component.html',
  styleUrl: './certificate-sync-button.component.scss'
})
export class CertificateSyncButtonComponent implements OnDestroy {
  /** Business taxId from the form — read again on every click. */
  @Input() taxId: string | null | undefined;
  /**
   * The tax id is the signed-in person's own id (an individual business): identity was proven at
   * registration, so ThaiD is not asked again and the sync runs for the current user.
   */
  @Input() ownIdentity = false;
  /** Only a สหกรณ์ still has to prove itself through ThaiD before its certificates are pulled. */
  @Input() requireThaid = false;
  @Output() synced = new EventEmitter<CertificateItemDto[]>();

  private thaidChannel: BroadcastChannel | null = null;
  private thaidPopup: Window | null = null;

  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);
  private readonly certificateApi = inject(UserCertificateApiService);

  isSyncing = false;

  sync(): void {
    const taxId = `${this.taxId ?? ''}`.trim();
    if (!taxId || this.isSyncing) {
      return;
    }
    this.confirmDialog.open({
      title: 'ซิงค์ใบรับรอง',
      content: 'ต้องการซิงค์ใบรับรองจาก ThaiTraces ใช่หรือไม่',
      confirmButtonMsg: 'ซิงค์',
      closeButtonMsg: 'ยกเลิก'
    }).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      // An individual proved who they are at registration; a juristic / community-enterprise
      // registration number is looked up as-is. Only a สหกรณ์ goes through ThaiD first.
      if (this.ownIdentity) {
        this.syncForCurrentUser();
        return;
      }
      if (this.requireThaid) {
        setTimeout(() => this.startThaidAuth(taxId));
        return;
      }
      this.syncByTaxId(taxId);
    });
  }

  private startThaidAuth(taxId: string): void {
    if (this.thaidPopup && !this.thaidPopup.closed) {
      this.thaidPopup.focus();
      return;
    }
    this.thaidPopup = window.open(
      '/public/api/thaid/initial',
      'thaid-auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );
    this.closeThaidChannel();
    this.thaidChannel = new BroadcastChannel('thaid-auth');
    this.thaidChannel.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'THAID_AUTH_SUCCESS') {
        const data = event.data.payload as ThaidAuthData;
        this.closeThaidChannel();
        if (data) {
          this.syncByTaxId(taxId);
        }
      }
    };
  }

  ngOnDestroy(): void {
    this.closeThaidChannel();
  }

  private closeThaidChannel(): void {
    if (this.thaidChannel) {
      this.thaidChannel.close();
      this.thaidChannel = null;
    }
  }

  private syncByTaxId(taxId: string): void {
    this.isSyncing = true;
    this.certificateApi.syncCertificatesByTaxId(taxId).subscribe({
      next: (items) => {
        this.isSyncing = false;
        this.toast.success('ซิงค์ใบรับรองเรียบร้อยแล้ว');
        this.synced.emit(items);
      },
      error: () => {
        this.isSyncing = false;
      }
    });
  }

  private syncForCurrentUser(): void {
    this.isSyncing = true;
    this.certificateApi.syncCertificates().subscribe({
      next: (items) => {
        this.isSyncing = false;
        this.toast.success('ซิงค์ใบรับรองเรียบร้อยแล้ว');
        this.synced.emit(items);
      },
      error: () => {
        this.isSyncing = false;
      }
    });
  }
}
